import { useState, useEffect } from 'react';
import Factories from './Factories';
import Users from './Users';
import Orders from './Orders';
import NewOrder from './NewOrder';
import Tasks from './Tasks';
import Urlopy from './Urlopy';
import Logs from './Logs';
import api from '../api';

export default function Dashboard({ user, onLogout }) {
  const isAdmin = user.rola === 'ADMIN';
  const [activePage, setActivePage] = useState('orders');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [taskCounts, setTaskCounts] = useState({ doZrobienia: 0, wTrakcie: 0 });

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const refreshTaskCounts = () => {
    api.get('/tasks').then(res => {
      const mine = res.data.filter(t => t.assigned_to === user.id);
      setTaskCounts({
        doZrobienia: mine.filter(t => t.status === 'DO_ZROBIENIA').length,
        wTrakcie:    mine.filter(t => t.status === 'W_TRAKCIE').length,
      });
    }).catch(() => {});
  };

  useEffect(() => { refreshTaskCounts(); }, [user.id]); // eslint-disable-line

  const menuItems = [
    { key: 'orders', label: '📦 Zamówienia' },
    { key: 'tasks',  label: '✅ Do zrobienia' },
    { key: 'urlopy', label: '📅 Kalendarz' },
    ...(isAdmin ? [{ key: '__sep__' }, { key: 'factories', label: '🏭 Fabryki' }, { key: 'users', label: '👥 Użytkownicy' }, { key: 'logs', label: '📋 Logi' }] : []),
  ];

  const handleNavClick = (key) => {
    setActivePage(key);
    if (isMobile) setSidebarOpen(false);
  };

  const renderPage = () => {
    switch (activePage) {
      case 'orders': return <Orders onNewOrder={() => setActivePage('new-order')} />;
      case 'new-order': return isAdmin ? <NewOrder onSave={() => setActivePage('orders')} onCancel={() => setActivePage('orders')} /> : null;
      case 'tasks': return <Tasks user={user} onTasksChanged={refreshTaskCounts} />;
      case 'urlopy': return <Urlopy user={user} />;
      case 'factories': return isAdmin ? <Factories /> : null;
      case 'users': return isAdmin ? <Users /> : null;
      case 'logs': return isAdmin ? <Logs /> : null;
      default: return <Orders onNewOrder={() => setActivePage('new-order')} />;
    }
  };

  const sidebarStyle = isMobile
    ? {
        ...s.sidebar,
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        zIndex: 200,
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 250ms ease',
      }
    : {
        ...s.sidebar,
        width: sidebarOpen ? '220px' : '0',
        minWidth: 0,
        overflow: 'hidden',
        transition: 'width 250ms ease',
      };

  return (
    <div style={s.layout}>
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div style={s.overlay} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div style={sidebarStyle}>
        <div style={s.logo}>
          <span style={s.logoIcon}>📦</span>
          <span style={s.logoText}>System zamówień do fabryk Timemaster</span>
        </div>
        <nav style={s.nav}>
          {menuItems.map(item => item.key === '__sep__' ? (
            <div key="__sep__" style={s.navSep} />
          ) : (
            <button
              key={item.key}
              style={activePage === item.key ? { ...s.navItem, ...s.navItemActive } : s.navItem}
              onClick={() => handleNavClick(item.key)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={s.userInfo}>
            <div style={s.userAvatar}>{user.imie.charAt(0).toUpperCase()}</div>
            <div>
              <div style={s.userName}>{user.imie}</div>
              <div style={s.userRole}>{user.rola}</div>
            </div>
          </div>
          <button style={s.logoutBtn} onClick={onLogout} title="Wyloguj">⏻</button>
        </div>
      </div>

      {/* Main content */}
      <div style={s.main}>
        <div style={s.topbar}>
          <button style={s.hamburger} onClick={() => setSidebarOpen(o => !o)}>☰</button>
          {(taskCounts.doZrobienia > 0 || taskCounts.wTrakcie > 0) && (
            <div style={s.taskBadges}>
              <span style={s.taskBadgeLabel}>Moje zadania:</span>
              {taskCounts.doZrobienia > 0 && (
                <span style={s.badgeBlue}>{taskCounts.doZrobienia} do zrobienia</span>
              )}
              {taskCounts.wTrakcie > 0 && (
                <span style={s.badgeOrange}>{taskCounts.wTrakcie} w trakcie</span>
              )}
            </div>
          )}
        </div>
        <div style={s.content}>
          {renderPage()}
        </div>
      </div>
    </div>
  );
}

const s = {
  layout: { display: 'flex', height: '100vh', overflow: 'hidden', background: '#0d1117' },
  sidebar: { width: '220px', background: '#001529', display: 'flex', flexDirection: 'column', flexShrink: 0, height: '100vh' },
  logo: { display: 'flex', alignItems: 'center', gap: '10px', padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  logoIcon: { fontSize: '22px', flexShrink: 0 },
  logoText: { color: 'white', fontWeight: '700', fontSize: '12px', lineHeight: '1.3' },
  nav: { flex: 1, padding: '12px 0' },
  navSep: { margin: '8px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' },
  navItem: { display: 'block', width: '100%', padding: '11px 16px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.65)', fontSize: '14px', textAlign: 'left', cursor: 'pointer', borderRadius: '0', whiteSpace: 'nowrap' },
  navItemActive: { background: '#1890ff', color: 'white' },
  sidebarFooter: { padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  userInfo: { display: 'flex', alignItems: 'center', gap: '10px' },
  userAvatar: { width: '32px', height: '32px', borderRadius: '50%', background: '#1890ff', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '14px', flexShrink: 0 },
  userName: { color: 'white', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap' },
  userRole: { color: 'rgba(255,255,255,0.45)', fontSize: '11px', whiteSpace: 'nowrap' },
  logoutBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', fontSize: '18px', cursor: 'pointer', padding: '4px' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100vh', overflow: 'hidden' },
  topbar: { display: 'flex', alignItems: 'center', padding: '8px 12px', background: '#161b22', borderBottom: '1px solid #30363d', flexShrink: 0, gap: '12px' },
  taskBadges: { display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' },
  taskBadgeLabel: { fontSize: '12px', color: '#8b949e' },
  badgeBlue:   { fontSize: '12px', fontWeight: '600', background: '#1c2b3a', color: '#58a6ff', border: '1px solid #1f4068', borderRadius: '4px', padding: '2px 8px' },
  badgeOrange: { fontSize: '12px', fontWeight: '600', background: '#7c3500', color: '#ff8c00', border: '1px solid #a04800', borderRadius: '4px', padding: '2px 8px' },
  content: { flex: 1, overflowY: 'auto', minHeight: 0 },
  hamburger: { background: 'none', border: 'none', color: '#c9d1d9', fontSize: '20px', cursor: 'pointer', padding: '4px 8px', lineHeight: 1, borderRadius: '4px' },
  overlay: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 199 },
};
